import React, { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "../../../lib/utils";
import {
  Activity,
  Clock,
  Server,
  Timer,
  Copy,
  ChevronDown,
  ChevronRight,
  Zap,
} from "lucide-react";
import GatewayStatCard from "../components/GatewayStatCard";
import GatewayFlowDiagram from "../components/GatewayFlowDiagram";
import GatewayJsonViewer from "../components/GatewayJsonViewer";
import GatewayStatusBadge from "../components/GatewayStatusBadge";
import { GatewayDashboardApi } from "../Api/gatewayApi";
import {
  timeAgo,
  copyToClipboard,
} from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import "../AgenticGateway.css";


interface ActiveEntry {
  correlationId: string;
  agentSessionId?: string;
  clientSessionId?: string;
  requestId?: string;
  agentName?: string;
  agentVersion?: string;
  publicName?: string;
  serverName?: string;
  originalToolName?: string;
  tokenMode?: string;
  startedAt: string;
  elapsedMs?: number;
  requestArgs?: any;
  responseSummary?: any;
  responseContentCount?: number;
  responseContentTypes?: string;
  registryLookupMs?: number;
  forwardCallMs?: number;
}

interface HistoryEntry extends ActiveEntry {
  status?: string;
  completedAt?: string;
  durationMs?: number;
  errorMessage?: string;
}


function formatElapsed(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

function elapsedColorClass(ms: number): string {
  if (ms < 2000) return "text-emerald-600 dark:text-emerald-400";
  if (ms < 10000) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

// >30s = "stuck": red-tinted row background (matches OG row-stuck emphasis)
function isStuck(ms: number): boolean {
  return ms > 30000;
}

// backend tokenMode values: PENDING (default) | STS-OBO | AGENT-PROVIDED | CONFIG
function TokenModeBadge({ mode }: { mode?: string }) {
  if (mode === "STS-OBO")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
        OBO Token
      </span>
    );
  if (mode === "AGENT-PROVIDED")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30">
        Agent Token
      </span>
    );
  if (mode === "PENDING")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted-foreground/15 text-muted-foreground border border-muted-foreground/30">
        Pending
      </span>
    );
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted-foreground/15 text-muted-foreground border border-muted-foreground/30">
      Config
    </span>
  );
}

function agentDisplay(name?: string, version?: string): string {
  if (name && name !== "unknown") {
    return `${name}${version ? " v" + version : ""}`;
  }
  return "Unknown";
}


interface DetailPanelProps {
  entry: ActiveEntry | HistoryEntry;
  isActive: boolean;
}

function DetailPanel({ entry, isActive }: DetailPanelProps) {
  const { toast } = useToast();
  const totalMs = (entry as HistoryEntry).durationMs || 0;
  const lookupMs = entry.registryLookupMs || 0;
  const forwardMs = entry.forwardCallMs || 0;
  const otherMs = Math.max(0, totalMs - lookupMs - forwardMs);
  const hasTimings = totalMs > 0;

  const lookupPct = totalMs > 0 ? Math.max(2, (lookupMs / totalMs) * 100) : 0;
  const forwardPct = totalMs > 0 ? Math.max(2, (forwardMs / totalMs) * 100) : 0;
  const otherPct = Math.max(2, 100 - lookupPct - forwardPct);

  const handleCopy = async (text: string) => {
    await copyToClipboard(text);
    toast({ title: "Copied to clipboard" });
  };

  return (
    <div className="p-4 space-y-4 ag-surface rounded-b-xl border-t border-border">
      <GatewayFlowDiagram
        agentName={entry.agentName}
        serverName={entry.serverName}
        sessionId={entry.agentSessionId}
        clientSessionId={entry.clientSessionId}
        requestId={entry.requestId}
        correlationId={entry.correlationId}
        originalToolName={entry.originalToolName}
        tokenMode={entry.tokenMode}
      />

      <div className="grid grid-cols-2 gap-3">
        {entry.requestArgs && (
          <GatewayJsonViewer
            data={entry.requestArgs}
            label="Request Payload"
            collapsed={false}
            maxHeight="200px"
          />
        )}
        {isActive ? (
          <div className="rounded-lg border border-border ag-surface p-3 flex items-center gap-2 text-sm text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            Awaiting response...
          </div>
        ) : (entry as HistoryEntry).responseSummary || (entry.responseContentCount || 0) > 0 ? (
          <GatewayJsonViewer
            data={(entry as HistoryEntry).responseSummary}
            label={`Response (${entry.responseContentCount || 0} item${(entry.responseContentCount || 0) !== 1 ? "s" : ""}, ${entry.responseContentTypes || "unknown"})`}
            collapsed={false}
            maxHeight="200px"
          />
        ) : null}
      </div>

      {(entry as HistoryEntry).errorMessage && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          <strong>Error:</strong> {(entry as HistoryEntry).errorMessage}
        </div>
      )}

      {hasTimings && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Timing Breakdown</p>
          <div className="flex h-4 rounded-full overflow-hidden gap-0.5">
            <div
              className="bg-primary/70 flex items-center justify-center text-[9px] text-white"
              style={{ width: `${lookupPct}%` }}
              title={`Registry Lookup: ${lookupMs}ms`}
            >
              {lookupMs > 0 ? lookupMs + "ms" : ""}
            </div>
            <div
              className="bg-blue-500/70 flex items-center justify-center text-[9px] text-white"
              style={{ width: `${forwardPct}%` }}
              title={`Forward Call: ${forwardMs}ms`}
            >
              {forwardMs}ms
            </div>
            <div
              className="bg-muted-foreground/50 flex items-center justify-center text-[9px] text-white"
              style={{ width: `${otherPct}%` }}
              title={`Overhead: ${otherMs}ms`}
            >
              {otherMs > 0 ? otherMs + "ms" : ""}
            </div>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
            <span><span className="inline-block w-2 h-2 rounded-full bg-primary/70 mr-1" />Lookup {lookupMs}ms</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500/70 mr-1" />Forward {forwardMs}ms</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/50 mr-1" />Overhead {otherMs}ms</span>
            <span className="ml-auto text-muted-foreground">Total: {totalMs}ms</span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => handleCopy(entry.correlationId)}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground/80 hover:border-border transition-colors"
        >
          <Copy className="w-3 h-3" /> Copy Correlation ID
        </button>
        {entry.agentSessionId && (
          <button
            onClick={() => handleCopy(entry.agentSessionId!)}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground/80 hover:border-border transition-colors"
          >
            <Copy className="w-3 h-3" /> Copy Agent Session
          </button>
        )}
        {entry.clientSessionId && (
          <button
            onClick={() => handleCopy(entry.clientSessionId!)}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground/80 hover:border-border transition-colors"
          >
            <Copy className="w-3 h-3" /> Copy Client Session
          </button>
        )}
      </div>

      {isActive && entry.agentSessionId && (
        <p className="text-[11px] text-muted-foreground/80 pt-0.5">
          To stop this agent, use <span className="text-foreground/80 font-medium">Kill</span> on the Agents &rsaquo; Live Sessions page.
        </p>
      )}
    </div>
  );
}


export default function GatewayInflightPage() {
  const { toast } = useToast();
  const [activeEntries, setActiveEntries] = useState<ActiveEntry[]>([]);
  const [recentHistory, setRecentHistory] = useState<HistoryEntry[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [elapsedMap, setElapsedMap] = useState<Record<string, number>>({});

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tickTimerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const data = await GatewayDashboardApi.inFlight();
      const entries: ActiveEntry[] = data.entries || data.active || [];
      const history: HistoryEntry[] = data.recentHistory || [];
      setActiveEntries(entries);
      setRecentHistory(history);
      setActiveCount(data.activeCount ?? entries.length);
      setLoadError(null);

      const map: Record<string, number> = {};
      entries.forEach((e) => {
        map[e.correlationId] = e.elapsedMs || (Date.now() - new Date(e.startedAt).getTime());
      });
      setElapsedMap(map);
    } catch (err: any) {
      setLoadError(err?.message || "Failed to load in-flight data");
    }
  }, []);

  const tick = useCallback(() => {
    setElapsedMap((prev) => {
      const next = { ...prev };
      setActiveEntries((entries) => {
        entries.forEach((e) => {
          next[e.correlationId] = Date.now() - new Date(e.startedAt).getTime();
        });
        return entries;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    fetchData();
    refreshTimerRef.current = setInterval(fetchData, 3000);
    tickTimerRef.current = setInterval(tick, 1000);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    };
  }, [fetchData, tick]);

  const toggleRow = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleCopy = async (text: string) => {
    await copyToClipboard(text);
    toast({ title: "Copied to clipboard" });
  };

  const longestMs = activeEntries.length
    ? Math.max(...activeEntries.map((e) => elapsedMap[e.correlationId] || 0))
    : 0;
  const uniqueServers = new Set(activeEntries.map((e) => e.serverName || "")).size;
  const avgElapsed = activeEntries.length
    ? Math.round(
        activeEntries.reduce((s, e) => s + (elapsedMap[e.correlationId] || 0), 0) /
          activeEntries.length
      )
    : 0;

  if (loadError) {
    return (
      <div className="ag-page">
        <div className="ag-card ag-content">
          <div className="ag-empty h-full">
            <span className="ag-empty-icon text-amber-500" aria-hidden="true">
              <Activity />
            </span>
            <p>{loadError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ag-page">
      <div className="ag-card ag-toolbar">
        <div className="ag-toolbar-group">
          <span className="ag-live" data-tone={activeCount > 0 ? "primary" : "muted"}>
            <span className={cn("ag-live-dot", activeCount > 0 && "animate-pulse")} aria-hidden="true" />
            {activeCount} active request{activeCount !== 1 ? "s" : ""}
          </span>
          <span className="ag-meta">
            <Clock aria-hidden="true" />
            Auto-refresh: 3s
          </span>
        </div>
        <span className="ag-summary">
          <Timer aria-hidden="true" />
          <span>Recent activity <strong>{recentHistory.length}</strong> of last 50</span>
        </span>
      </div>

      {activeEntries.length > 0 && (
        <div className="ag-stat-grid">
          <GatewayStatCard
            icon={<Activity />}
            label="Active"
            value={activeCount}
            colorClass="text-primary"
          />
          <GatewayStatCard
            icon={<Clock />}
            label="Longest Running"
            value={formatElapsed(longestMs)}
            colorClass={longestMs > 10000 ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}
          />
          <GatewayStatCard
            icon={<Server />}
            label="Servers In Use"
            value={uniqueServers}
            colorClass="text-blue-600 dark:text-blue-400"
          />
          <GatewayStatCard
            icon={<Timer />}
            label="Avg Elapsed"
            value={formatElapsed(avgElapsed)}
            colorClass="text-foreground/80"
          />
        </div>
      )}

      <div className="ag-card ag-content">
        <div className="ag-scroll space-y-6">
          <section className="space-y-3">
            <span className="ag-section-label">
              <Zap aria-hidden="true" />
              Active Requests
            </span>
            {activeEntries.length === 0 ? (
              <div className="ag-empty min-h-[160px] rounded-xl border border-dashed border-border">
                <span className="ag-empty-icon" aria-hidden="true">
                  <Zap />
                </span>
                <p>No active requests — gateway idle</p>
              </div>
            ) : (
              <>
                <div className="ag-table-wrap">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border bg-card">
                        <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          Correlation ID
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          Agent
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          Public Tool
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          Target Server
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          Original Tool
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          Auth Mode
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          Elapsed
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeEntries.map((entry) => {
                        const elapsed = elapsedMap[entry.correlationId] || 0;
                        const isExpanded = expandedId === entry.correlationId;
                        return (
                          <React.Fragment key={entry.correlationId}>
                            <tr
                              onClick={() => toggleRow(entry.correlationId)}
                              className={cn(
                                "border-b border-border hover:bg-primary/[0.025] cursor-pointer transition-colors",
                                isStuck(elapsed) && "bg-red-500/5"
                              )}
                            >
                              <td className="px-3 py-2.5 font-mono">
                                <div className="flex items-center gap-1.5">
                                  {isExpanded ? (
                                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                  )}
                                  <span
                                    className="text-foreground/80 truncate max-w-[80px]"
                                    title={entry.correlationId}
                                  >
                                    {entry.correlationId.slice(0, 8)}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopy(entry.correlationId);
                                    }}
                                    className="p-0.5 rounded hover:bg-primary/[0.025] text-muted-foreground/70 hover:text-muted-foreground"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-foreground/80">
                                {agentDisplay(entry.agentName, entry.agentVersion)}
                              </td>
                              <td className="px-3 py-2.5 font-medium text-foreground">
                                {entry.publicName || "-"}
                              </td>
                              <td className="px-3 py-2.5 text-muted-foreground">
                                {entry.serverName || "-"}
                              </td>
                              <td className="px-3 py-2.5 text-muted-foreground">
                                {entry.originalToolName || "-"}
                              </td>
                              <td className="px-3 py-2.5">
                                <TokenModeBadge mode={entry.tokenMode} />
                              </td>
                              <td className={cn("px-3 py-2.5 font-semibold tabular-nums", elapsedColorClass(elapsed))}>
                                {formatElapsed(elapsed)}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={7} className="p-0">
                                  <DetailPanel entry={entry} isActive={true} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          <section className="space-y-3">
            <span className="ag-section-label">
              <Timer aria-hidden="true" />
              Recent Activity — {recentHistory.length} of last 50
            </span>

            {recentHistory.length === 0 ? (
              <p className="text-xs text-muted-foreground/70 text-center py-4">No completed requests yet</p>
            ) : (
              <div className="ag-table-wrap">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border bg-card">
                      {["Status", "Correlation ID", "Agent", "Public Tool", "Server", "Auth Mode", "Duration", "Completed", "Error"].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {recentHistory.map((h) => {
                      const isExpanded = expandedId === h.correlationId;
                      const rowFailed = h.status === "FAILED" || h.status === "ERROR";
                      return (
                        <React.Fragment key={h.correlationId}>
                          <tr
                            onClick={() => toggleRow(h.correlationId)}
                            className={cn(
                              "border-b border-border hover:bg-primary/[0.025] cursor-pointer transition-colors",
                              rowFailed && "bg-red-500/5"
                            )}
                          >
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-1">
                                {isExpanded ? (
                                  <ChevronDown className="w-3 h-3 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="w-3 h-3 text-muted-foreground" />
                                )}
                                <GatewayStatusBadge status={h.status || "UNKNOWN"} />
                              </div>
                            </td>
                            <td className="px-3 py-2.5 font-mono">
                              <div className="flex items-center gap-1">
                                <span className="text-foreground/80" title={h.correlationId}>
                                  {h.correlationId.slice(0, 8)}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(h.correlationId);
                                  }}
                                  className="p-0.5 rounded hover:bg-primary/[0.025] text-muted-foreground/70 hover:text-muted-foreground"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-foreground/80">
                              {agentDisplay(h.agentName, h.agentVersion)}
                            </td>
                            <td className="px-3 py-2.5 font-medium text-foreground">
                              {h.publicName || "-"}
                            </td>
                            <td className="px-3 py-2.5 text-muted-foreground">{h.serverName || "-"}</td>
                            <td className="px-3 py-2.5">
                              <TokenModeBadge mode={h.tokenMode} />
                            </td>
                            <td className={cn("px-3 py-2.5 tabular-nums font-medium",
                              h.durationMs !== undefined ? (
                                h.durationMs < 2000 ? "text-emerald-600 dark:text-emerald-400" :
                                h.durationMs < 10000 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"
                              ) : "text-muted-foreground"
                            )}>
                              {h.durationMs !== undefined ? formatElapsed(h.durationMs) : "-"}
                            </td>
                            <td className="px-3 py-2.5 text-muted-foreground">
                              {h.completedAt ? timeAgo(h.completedAt) : "-"}
                            </td>
                            <td className="px-3 py-2.5">
                              {h.errorMessage ? (
                                <span className="text-red-600 dark:text-red-400 truncate max-w-[120px] block" title={h.errorMessage}>
                                  {h.errorMessage.length > 40
                                    ? h.errorMessage.slice(0, 40) + "..."
                                    : h.errorMessage}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/70">-</span>
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={9} className="p-0">
                                <DetailPanel entry={h} isActive={false} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
